/**
 * pagesSansLeurPartie — une annulation a-t-elle laissé des pages sans un en-tête
 * ou un pied qu'elles montraient ?
 *
 * `set_header_footer` sur une première page (ou sur les pages paires) fait
 * plusieurs écritures : le réglage de section, la copie de l'autre partie, la
 * zone demandée, ses paragraphes. Mesuré le 2026-10-05
 * (`tests/ws-mocked/mesure-annulation-entete-et-pied.spec.ts`, gabarit Hector,
 * en-tête de première page avec copie du pied) :
 *
 *  - l'appel laisse HUIT entrées d'historique, une par écriture ;
 *  - `history.undo` en défait une à la fois, dans l'ordre inverse ;
 *  - huit annulations rendent le fichier de départ (parties et `w:titlePg`) ;
 *  - six ou sept laissent le réglage levé SANS la copie : `w:titlePg` présent,
 *    aucun pied de première page — la page 1 ne numérote plus.
 *
 * L'historique n'a pas de groupe à défaire d'un bloc : l'état intermédiaire est
 * atteignable par un `undo_changes` au compte trop court. Le reçu de
 * l'annulation le dit, à partir de deux relevés des zones (avant, après).
 *
 * La règle compare, elle ne juge pas un état seul : une page 1 sans pied est
 * aussi ce que laisse le papier à en-tête du cabinet (`canvasLetterhead.ts`),
 * et une annulation qui n'y a pas touché n'a rien à en dire.
 */

import type { CanvasHeaderFooterType } from '@hector/shared-types';
import { lireLeStatutDeSlot, operationDuDocument, slotDEnTete } from './canvasSnapshot';

type StatutDeZone = 'explicit' | 'inherited' | 'none';

/** Les réglages d'une section et le statut de ses six zones, tels que l'éditeur les résout. */
export interface ZonesDeSection {
  /** `null` : l'éditeur ne publie pas le réglage (jamais levé) — non lisible, pas « faux ». */
  premierePageDistincte: boolean | null;
  pagesPairesDistinctes: boolean | null;
  header: Record<CanvasHeaderFooterType, StatutDeZone>;
  footer: Record<CanvasHeaderFooterType, StatutDeZone>;
}

/** Une page (première, ou paires) qui ne montre plus une partie qu'elle montrait. */
export interface PagePriveeDUnePartie {
  section_index: number;
  part: 'header' | 'footer';
  zone: 'first_page' | 'even_pages';
}

function estEnregistrement(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur);
}

const booleenPublie = (valeur: unknown): boolean | null => (typeof valeur === 'boolean' ? valeur : null);

/**
 * Relève les zones de chaque section. `null` : l'éditeur n'expose pas les sections ou leurs zones, ou la
 * lecture a échoué — l'appelant ne conclut alors rien.
 */
export async function lireLesZonesDePage(doc: unknown): Promise<ZonesDeSection[] | null> {
  const lister = operationDuDocument(doc, 'sections', 'list');
  const resoudre = operationDuDocument(doc, 'headerFooters', 'resolve');
  if (lister === undefined || resoudre === undefined) return null;
  try {
    const liste: unknown = await lister({});
    const sections = estEnregistrement(liste) && Array.isArray(liste.items) ? liste.items.filter(estEnregistrement) : [];
    const relevees: ZonesDeSection[] = [];
    for (const entree of sections) {
      const adresse = estEnregistrement(entree.address) ? entree.address : entree;
      if (typeof adresse.sectionId !== 'string') return null;
      const section = { kind: 'section' as const, sectionId: adresse.sectionId };
      const zonesDe = async (partie: 'header' | 'footer'): Promise<Record<CanvasHeaderFooterType, StatutDeZone>> => ({
        primary: lireLeStatutDeSlot(await resoudre({ target: slotDEnTete(section, partie, 'primary') })),
        first_page: lireLeStatutDeSlot(await resoudre({ target: slotDEnTete(section, partie, 'first_page') })),
        even_pages: lireLeStatutDeSlot(await resoudre({ target: slotDEnTete(section, partie, 'even_pages') })),
      });
      relevees.push({
        premierePageDistincte: booleenPublie(entree.titlePage),
        pagesPairesDistinctes: booleenPublie(entree.oddEvenHeadersFooters),
        header: await zonesDe('header'),
        footer: await zonesDe('footer'),
      });
    }
    return relevees;
  } catch {
    return null;
  }
}

/**
 * Les pages qui, APRÈS, ne montrent plus une partie qu'elles montraient AVANT : le réglage de section est
 * toujours levé, la zone de ces pages a disparu, et les autres pages montrent toujours la partie.
 */
export function pagesPriveesDUnePartie(
  avant: readonly ZonesDeSection[],
  apres: readonly ZonesDeSection[],
): PagePriveeDUnePartie[] {
  const privees: PagePriveeDUnePartie[] = [];
  apres.forEach((section, rang) => {
    const dAvant = avant[rang];
    if (dAvant === undefined) return;
    for (const [zone, leve] of [['first_page', section.premierePageDistincte], ['even_pages', section.pagesPairesDistinctes]] as const) {
      if (leve !== true) continue;
      for (const part of ['header', 'footer'] as const) {
        if (dAvant[part][zone] !== 'none' && section[part][zone] === 'none' && section[part].primary !== 'none') {
          privees.push({ section_index: rang, part, zone });
        }
      }
    }
  });
  return privees;
}
